'use client';

import { useShowMoreLess } from '@/hooks/useShowMoreLess';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { BlockComponentModel } from '@/cms/types/blok-component';
import Button from '@/components/atoms/Button';
import { passButtonIcons } from '@/components/atoms/Button/utils';
import { LinkButtonProps } from '@/components/atoms/LinkButton';
import AccordionListStrapi, {
  AccordionListStrapiProps,
} from '@/components/molecules/accordion/AccordionListStrapi';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';
import FAQSchemaScript from './FAQSchemaScript';

export interface FAQStrapiProps {
  header?: BlockHeader;
  faqs: AccordionListStrapiProps;
}

export default function FAQStrapi({
  block,
  ...rest
}: BlockComponentModel<FAQStrapiProps>) {
  const { faqs, header } = block;

  const {
    toggleShowMore,
    initiallyHiddenItems,
    initiallyVisibleItems,
    buttonText,
    showAction,
  } = useShowMoreLess({
    items: faqs.accordionList,
    initialShowCount: 6,
  });

  return (
    <BlockWrapper {...rest} data-tracker-section={SECTION_NAMES.FAQ}>
      <FAQSchemaScript accordionList={faqs.accordionList} />
      <BlockHeaderContent
        {...header}
        layoutOptions={{
          constrainWidthWithPadding: true,
        }}
        renderButtons={
          showAction
            ? [
                (props: Partial<LinkButtonProps>) => (
                  <Button
                    onClick={toggleShowMore}
                    key={buttonText + 'faq-strapi'}
                    {...passButtonIcons(props)}
                    variant={props.variant || 'outlined'}
                    color={props.color || 'secondary'}
                    data-tracker-component='show-more-less'
                  >
                    {buttonText}
                  </Button>
                ),
              ]
            : undefined
        }
      >
        <AccordionListStrapi
          block={{ accordionList: initiallyVisibleItems }}
          internal={{
            accordionItem: {
              classNames: { content: 'pr-6' },
            },
          }}
        />
        <AccordionListStrapi
          block={{ accordionList: initiallyHiddenItems }}
          internal={{
            accordionItem: {},
            ContainerProps: { className: 'hide-content' },
          }}
        />
      </BlockHeaderContent>
    </BlockWrapper>
  );
}
